/**
 * AdminLayout - Layout wrapper for all admin pages
 * 
 * Provides a consistent structure for the admin panel with:
 * - Sidebar navigation (collapsible on mobile)
 * - Header with user info and logout
 * - Main content area
 * 
 * This layout is separate from the public website layout
 * to provide a focused admin experience.
 */

import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  HiHome, 
  HiMenuAlt2, 
  HiX, 
  HiShoppingBag,
  HiTag,
  HiClipboardList,
  HiUsers,
  HiCog,
  HiLogout,
  HiPhotograph,
  HiGlobe,
  HiQuestionMarkCircle,
  HiAdjustments,
  HiClipboardCheck,
  HiBookOpen,
  HiCollection,
  HiColorSwatch,
  HiSparkles,
  HiChevronDown,
  HiChevronUp,
  HiDocumentReport,
  HiViewGrid,
  HiFolder,
  HiExternalLink,
  HiInbox,
  HiLink,
  HiCalendar,
  HiMail,
  HiDownload
} from 'react-icons/hi';
import { useAuth } from '../hooks/useAuth';
import SweetypieChatbot from '../components/SweetypieChatbot';
import AdminFlashAlert from '../components/AdminFlashAlert';
import AdminAlertSoundToggle from '../components/AdminAlertSoundToggle';
import { useAdminLiveAlerts } from '../hooks/useAdminLiveAlerts';
import { AdminAlertSoundProvider, useAdminAlertSound } from '../hooks/useAdminAlertSound';
import { usePageSeo } from '../../utils/seo';
import { adminFetch, shouldAttachAdminAuth } from '../lib/adminApi';

// Fetch pending order count for sidebar badge.
const usePendingOrderCount = () => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await adminFetch('/api/orders/stats');
        if (res.ok) {
          const data = await res.json();
          setCount(data.pending || 0);
        }
      } catch {
        /* badge hidden on failure */
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  return count;
};

/** Unread contact form submissions — sidebar badge on Contact messages. */
const useContactMessageCount = () => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await adminFetch('/api/contact/stats');
        if (res.ok) {
          const data = await res.json();
          setCount(data.new ?? 0);
        }
      } catch {
        /* badge hidden on failure */
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  return count;
};

interface AdminLayoutProps {
  children: ReactNode;
}

// Navigation structure with grouped items
interface NavItem {
  path: string;
  icon: typeof HiHome;
  label: string;
  exact?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
  defaultOpen?: boolean;
}

const navGroups: NavGroup[] = [
  {
    label: 'Main',
    defaultOpen: true,
    items: [
      { path: '/admin', icon: HiHome, label: 'Dashboard', exact: true },
      { path: '/admin/orders', icon: HiClipboardList, label: 'Orders' },
      { path: '/admin/messages', icon: HiInbox, label: 'Contact messages' },
      { path: '/admin/menu', icon: HiShoppingBag, label: 'Menu' },
      { path: '/admin/categories', icon: HiFolder, label: 'Categories' },
    ]
  },
  {
    label: 'Content',
    defaultOpen: true,
    items: [
      { path: '/admin/holidays', icon: HiSparkles, label: 'Holidays' },
      { path: '/admin/collections', icon: HiCollection, label: 'Special Menus' },
      { path: '/admin/gallery', icon: HiPhotograph, label: 'Gallery' },
      { path: '/admin/images', icon: HiViewGrid, label: 'All Images' },
      { path: '/admin/media', icon: HiDownload, label: 'Media' },
      { path: '/admin/articles', icon: HiBookOpen, label: 'Articles' },
      { path: '/admin/homepage-editor', icon: HiSparkles, label: 'Homepage Editor' },
      { path: '/admin/seasons', icon: HiCalendar, label: 'Seasonal Content' },
      { path: '/admin/campaigns', icon: HiLink, label: 'Fundraiser campaigns' },
    ]
  },
  {
    label: 'Products',
    defaultOpen: false,
    items: [
      { path: '/admin/product-options', icon: HiAdjustments, label: 'Product Options' },
      { path: '/admin/events', icon: HiCalendar, label: 'Events & Classes' },
      { path: '/admin/event-categories', icon: HiColorSwatch, label: 'Event Categories' },
    ]
  },
  {
    label: 'Sales & Customers',
    defaultOpen: false,
    items: [
      { path: '/admin/customers',     icon: HiUsers,          label: 'Customers' },
      { path: '/admin/email-signups', icon: HiMail,           label: 'Email List' },
      { path: '/admin/promos',        icon: HiTag,            label: 'Discounts & Promotions' },
      { path: '/admin/redemptions',   icon: HiClipboardCheck, label: 'Redemptions' },
    ]
  },
  {
    label: 'Settings',
    defaultOpen: false,
    items: [
      { path: '/admin/integrations', icon: HiGlobe, label: 'Integrations' },
      { path: '/admin/settings', icon: HiCog, label: 'Settings' },
      { path: '/admin/tax-reports', icon: HiDocumentReport, label: 'Tax & Reports' },
      { path: '/admin/help', icon: HiQuestionMarkCircle, label: 'Help Guide' },
    ]
  },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  return (
    <AdminAlertSoundProvider>
      <AdminLayoutInner>{children}</AdminLayoutInner>
    </AdminAlertSoundProvider>
  );
}

function AdminLayoutInner({ children }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { logout, user } = useAuth();
  const { soundEnabled } = useAdminAlertSound();
  const pendingOrderCount = usePendingOrderCount();
  const contactMessageCount = useContactMessageCount();
  /** Orders page has its own poll + print; avoid duplicate popups there. */
  const liveAlertsOnOtherPages = !location.pathname.startsWith('/admin/orders');
  const { flash, dismissOrder, dismissMessage } = useAdminLiveAlerts(liveAlertsOnOtherPages, soundEnabled);

  // Admin pages should not be indexed by search engines
  usePageSeo({
    title: "Admin | Nanny B's Bakery",
    description: 'Staff login and bakery management portal.',
    canonicalPath: location.pathname,
    robots: 'noindex, nofollow',
  });

  // Track which groups are expanded (default to defaultOpen state)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const state: Record<string, boolean> = {};
    navGroups.forEach((group) => {
      state[group.label] = group.defaultOpen ?? false;
    });
    return state;
  });

  // Check if a nav item is active
  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Toggle group expansion
  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel]
    }));
  };

  // Auto-expand group if any item in it is active
  useEffect(() => {
    navGroups.forEach(group => {
      const hasActiveItem = group.items.some(item => isActive(item.path, item.exact));
      if (hasActiveItem && !expandedGroups[group.label]) {
        setExpandedGroups(prev => ({
          ...prev,
          [group.label]: true
        }));
      }
    });
  }, [location.pathname]);

  // Attach session cookie + admin token to all /api calls from authenticated admin pages.
  useEffect(() => {
    const nativeFetch = window.fetch.bind(window);
    const ADMIN_TOKEN = import.meta.env.VITE_ARTICLES_ADMIN_TOKEN || '';

    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : String(input);
      if (!shouldAttachAdminAuth(url)) {
        return nativeFetch(input, init);
      }
      const headers = new Headers(init?.headers);
      if (ADMIN_TOKEN && !headers.has('x-admin-token')) {
        headers.set('x-admin-token', ADMIN_TOKEN);
      }
      return nativeFetch(input, { ...init, headers, credentials: 'include' });
    };

    return () => {
      window.fetch = nativeFetch;
    };
  }, []);

  // Handle logout - clears session and redirects
  const handleLogout = () => {
    logout();
    // Navigation happens automatically via ProtectedRoute
  };

  // Short title in the sticky mobile header (iPhone-sized screen).
  const mobilePageTitle = (() => {
    const path = location.pathname.replace(/\/$/, '') || '/admin';
    if (path === '/admin') return 'Dashboard';
    if (path.startsWith('/admin/orders')) return 'Orders';
    if (path.startsWith('/admin/messages')) return 'Messages';
    if (path.startsWith('/admin/menu')) return 'Menu';
    if (path.startsWith('/admin/help')) return 'Help';
    const hit = navGroups.flatMap((g) => g.items).find((item) => path.startsWith(item.path) && item.path !== '/admin');
    return hit?.label || 'Admin';
  })();

  const onOrders = location.pathname.startsWith('/admin/orders');
  const onMessages = location.pathname.startsWith('/admin/messages');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 z-50 h-full w-64 bg-purple-900 text-white
          flex flex-col
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* Sidebar header with branding */}
        <div className="p-4 border-b border-purple-800">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold">Tammy's Self Service</h1>
              <p className="text-xs text-purple-300">Nanny B's Bakery</p>
            </div>
            {/* Close button - mobile only — 44px tap target for iPhone */}
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center hover:bg-purple-800 rounded-lg"
              aria-label="Close sidebar"
            >
              <HiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Navigation with scrollable groups - min-h-0 so it scrolls inside flex; padding-bottom keeps last item visible above Logout */}
        <nav className="flex-1 min-h-0 overflow-y-auto p-4 pb-2 space-y-2">
          {navGroups.map((group) => {
            const isExpanded = expandedGroups[group.label];
            const hasActiveItem = group.items.some(item => isActive(item.path, item.exact));
            
            return (
              <div key={group.label} className="space-y-1">
                {/* Group header - collapsible */}
                <button
                  onClick={() => toggleGroup(group.label)}
                  className={`
                    w-full flex items-center justify-between px-3 py-3 rounded-lg
                    transition-colors duration-200
                    ${hasActiveItem
                      ? 'bg-purple-800/50 text-white' 
                      : 'text-purple-300 hover:bg-purple-800/30 hover:text-white'
                    }
                  `}
                >
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    {group.label}
                  </span>
                  {isExpanded ? (
                    <HiChevronUp className="w-4 h-4" />
                  ) : (
                    <HiChevronDown className="w-4 h-4" />
                  )}
                </button>

                {/* Group items - shown when expanded */}
                {isExpanded && (
                  <div className="ml-2 space-y-1 border-l-2 border-purple-800/30 pl-2">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path, item.exact);
                      const isOrdersPage = item.path === '/admin/orders';
                      const isMessagesPage = item.path === '/admin/messages';
                      const showBadge =
                        (isOrdersPage && pendingOrderCount > 0) ||
                        (isMessagesPage && contactMessageCount > 0);
                      const badgeCount = isMessagesPage ? contactMessageCount : pendingOrderCount;
                      
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`
                            flex items-center justify-between px-3 py-3 rounded-lg text-sm
                            transition-colors duration-200
                            ${active 
                              ? 'bg-purple-700 text-white' 
                              : 'text-purple-200 hover:bg-purple-800 hover:text-white'
                            }
                            ${showBadge ? 'animate-pulse bg-amber-600/20' : ''}
                          `}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            <span className="font-medium">{item.label}</span>
                          </div>
                          {showBadge && (
                            <span
                              className={`text-white text-xs font-bold px-2 py-0.5 rounded-full animate-bounce ${
                                isMessagesPage ? 'bg-blue-500' : 'bg-amber-500'
                              }`}
                            >
                              {badgeCount}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout button at bottom - flex-shrink-0 so it stays visible and nav scrolls above it */}
        <div className="flex-shrink-0 p-4 border-t border-purple-800" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg min-h-[44px]
                       text-purple-200 hover:bg-purple-800 hover:text-white
                       transition-colors duration-200"
          >
            <HiLogout className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:ml-64">
        {/* Top header — sticky under iPhone notch */}
        <header
          className="bg-white shadow-sm border-b sticky top-0 z-30"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="flex items-center justify-between px-3 py-2 sm:px-4 sm:py-3">
            {/* Mobile menu button — Apple HIG ~44px tap target */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center hover:bg-gray-100 rounded-lg"
              aria-label="Open menu"
            >
              <HiMenuAlt2 className="w-6 h-6 text-gray-600" />
            </button>

            <h2 className="text-lg font-semibold text-gray-800 lg:hidden truncate max-w-[40vw]">
              {mobilePageTitle}
            </h2>

            {/* User info / Quick actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              {!onOrders && <AdminAlertSoundToggle showLabel />}
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="min-h-[44px] min-w-[44px] sm:min-w-0 inline-flex items-center justify-center gap-1.5 text-sm text-purple-600 hover:text-purple-800 font-medium rounded-lg"
                aria-label="View site"
              >
                <HiExternalLink className="w-5 h-5" />
                <span className="hidden sm:inline">View site</span>
              </a>
              <span className="text-sm text-gray-600 hidden md:block">
                Welcome, {user?.email?.split('@')[0] || 'Tammy'}!
              </span>
              <div className="w-9 h-9 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {user?.email?.[0]?.toUpperCase() || 'T'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content — extra bottom pad clears iPhone tab bar + home indicator */}
        <main className="p-3 sm:p-4 lg:p-6 pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-6">
          {children}
        </main>
      </div>

      {/* iPhone thumb bar: Orders + Messages + More (opens full sidebar) */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Admin quick navigation"
      >
        <div className="grid grid-cols-3 max-w-lg mx-auto">
          <Link
            to="/admin/orders"
            className={`flex flex-col items-center justify-center gap-0.5 min-h-[52px] py-2 text-xs font-semibold ${
              onOrders ? 'text-purple-700' : 'text-gray-500'
            }`}
          >
            <span className="relative">
              <HiClipboardList className="w-6 h-6" />
              {pendingOrderCount > 0 && (
                <span className="absolute -top-1 -right-2 min-w-[1.1rem] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {pendingOrderCount > 9 ? '9+' : pendingOrderCount}
                </span>
              )}
            </span>
            Orders
          </Link>
          <Link
            to="/admin/messages"
            className={`flex flex-col items-center justify-center gap-0.5 min-h-[52px] py-2 text-xs font-semibold ${
              onMessages ? 'text-purple-700' : 'text-gray-500'
            }`}
          >
            <span className="relative">
              <HiInbox className="w-6 h-6" />
              {contactMessageCount > 0 && (
                <span className="absolute -top-1 -right-2 min-w-[1.1rem] h-4 px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {contactMessageCount > 9 ? '9+' : contactMessageCount}
                </span>
              )}
            </span>
            Messages
          </Link>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 min-h-[52px] py-2 text-xs font-semibold text-gray-500"
            aria-label="Open full menu"
          >
            <HiMenuAlt2 className="w-6 h-6" />
            More
          </button>
        </div>
      </nav>

      {/* Sweetypie Chatbot - Floating helper for Tammy */}
      <SweetypieChatbot />

      {/* Flashing popups on non-Orders admin pages (Orders has its own alerts). */}
      <AdminFlashAlert
        kind="order"
        open={flash.order.open}
        count={flash.order.count}
        onDismiss={dismissOrder}
      />
      <AdminFlashAlert
        kind="message"
        open={flash.message.open}
        count={flash.message.count}
        onDismiss={dismissMessage}
      />
    </div>
  );
}

